import React from 'react';
import {Table} from '@/web-components';
import {HIDDEN_FEATURES, REPORT_GROUPINGS} from '../../../../constants';
import Util from '../../../shared/util/util';
import {isFeatureHidden} from '../../../shared/util/CompanySetupUtil';
import {DeprecatedProjectIndicatorNoFragment} from '../../../shared/components/project-indicator/DeprecatedProjectIndicator';

export const getRoleName = timeReg => {
	if (timeReg.role) {
		return timeReg.role.name;
	} else {
		return timeReg.person.role ? timeReg.person.role.name : '';
	}
};

export const getClientName = timeReg => {
	if (timeReg.task) {
		if (timeReg.task.project.client) {
			return timeReg.task.project.client.name;
		}
	} else if (timeReg.project) {
		if (timeReg.project.client) {
			return timeReg.project.client.name;
		}
	} else return null;
};

export const getTimeRegTypeAndDetailDom = (timeReg, intl, isSharedView) => {
	if (timeReg.project) {
		const project = timeReg.project;
		return [
			project.fullAccessToProject ? (
				<DeprecatedProjectIndicatorNoFragment
					project={project}
					disableLink={isSharedView || project.readOnlyAccess}
					clearSubPath
					showProjectName
				/>
			) : (
				intl.formatMessage({id: 'timesheets.no_access'})
			),
			<span></span>,
		];
	} else if (timeReg.task) {
		const project = timeReg.task.project;
		return [
			project.fullAccessToProject ? (
				<DeprecatedProjectIndicatorNoFragment
					project={project}
					disableLink={isSharedView || project.readOnlyAccess}
					clearSubPath
					showProjectName
				/>
			) : (
				intl.formatMessage({id: 'timesheets.no_access'})
			),
			project.fullAccessToProject ? timeReg.task.name : ' ',
		];
	} else if (timeReg.idleTime && timeReg.idleTime.isInternalTime === true) {
		return [intl.formatMessage({id: 'common.internal_time'}), timeReg.idleTime.name];
	} else if (timeReg.idleTime && timeReg.idleTime.isInternalTime === false) {
		return [intl.formatMessage({id: 'common.time_off'}), timeReg.idleTime.name];
	}
};

export const getTimeRegTypeAndDetail = (timeReg, intl) => {
	if (timeReg.project) {
		return [
			timeReg.project.fullAccessToProject ? timeReg.project.name : intl.formatMessage({id: 'timesheets.no_access'}),
			'',
			timeReg.project.companyProjectId,
		];
	} else if (timeReg.task) {
		return [
			timeReg.task.project.fullAccessToProject
				? timeReg.task.project.name
				: intl.formatMessage({id: 'timesheets.no_access'}),
			timeReg.task.project.fullAccessToProject ? timeReg.task.name : ' ',
			timeReg.task.project.companyProjectId,
		];
	} else if (timeReg.idleTime && timeReg.idleTime.isInternalTime === true) {
		return [intl.formatMessage({id: 'common.internal_time'}), timeReg.idleTime.name, ''];
	} else if (timeReg.idleTime && timeReg.idleTime.isInternalTime === false) {
		return [intl.formatMessage({id: 'common.time_off'}), timeReg.idleTime.name, ''];
	}
};

export const noGroupingFirstDropdownOptions = intl => {
	return [
		{value: REPORT_GROUPINGS.PERSON, label: intl.formatMessage({id: 'common.group_by_person'})},
		{value: REPORT_GROUPINGS.PROJECT, label: intl.formatMessage({id: 'common.group_by_project'})},
		{value: REPORT_GROUPINGS.TASK, label: intl.formatMessage({id: 'common.group_by_task'})},
		...(!isFeatureHidden(HIDDEN_FEATURES.CLIENT)
			? [{value: REPORT_GROUPINGS.CLIENT, label: intl.formatMessage({id: 'common.group_by_client'})}]
			: []),
		{value: REPORT_GROUPINGS.DEPARTMENT, label: intl.formatMessage({id: 'common.group_by_department'})},
		{value: REPORT_GROUPINGS.ROLE, label: intl.formatMessage({id: 'common.group_by_role'})},
		{value: null, label: intl.formatMessage({id: 'project_sprints.group_by_people_disable'})},
	];
};

/**
 * The one place the report table's column skeleton is laid out.
 *
 * The table is positional: `TableColumns` zips a row's children against
 * `BaseLevelTableHeader`'s column list by index, and the first column is
 * `flex={1}`. A row that emits fewer cells than the header declares does not
 * merely lose its last cell — the flex column absorbs the leftover width and
 * every value in that row renders one column to the right of its title.
 *
 * Grouped rows and the whole-table totals row therefore pass only the cells
 * they fill and let this builder supply the rest, so adding a column to the
 * header is one edit here rather than nine across the row builders.
 *
 * Slot names match the header titles in order. `customFieldDefinitions` is the
 * same list the header maps, so the blank tail is always exactly as long as the
 * header's custom-field columns.
 */
export const getGroupedRowColumns = (cells = {}, customFieldDefinitions = []) => {
	const slots = [
		'label',
		'task',
		'phase',
		'deliverable',
		'sprint',
		'actualHours',
		'billableHours',
		'nonBillableHours',
		'notes',
		'date',
		'person',
		'role',
		'department',
		'client',
		'workIsBillable',
		'actualBillableValueOfService',
		'actualCost',
		'invoiced',
		'approved',
		'timeApprovalStatus',
	];

	const columns = slots.map(slot => <Table.Column key={slot}>{cells[slot]}</Table.Column>);

	for (const {key} of customFieldDefinitions ?? []) {
		columns.push(<Table.Column key={`customField-${key}`} />);
	}

	return columns;
};

/**
 * The custom-field tail for a row that carries values.
 *
 * The header emits one column per definition, so the tail must too. Values are
 * looked up by key rather than taken in the order the row happens to return
 * them: a row can carry fewer values than there are definitions, and its values
 * are not necessarily in definition order.
 */
export const getCustomFieldCells = (customFieldDefinitions = [], values = []) => {
	const valueByKey = new Map((values ?? []).map(value => [value.key, value]));

	return (customFieldDefinitions ?? []).map(({key}) => (
		<Table.Column key={`customField-${key}`}>
			<Table.Column.Cell>{valueByKey.get(key)?.value}</Table.Column.Cell>
		</Table.Column>
	));
};

/**
 * A money cell for the report: the formatted amount, or a dash when the viewer
 * cannot see the figure at all.
 *
 * `totalPrice` and `totalCost` are nullable in the schema and nothing refetches
 * them, so a missing value is rendered as zero rather than as a spinner that
 * would never resolve. Every money cell in the table — grouped rows and the
 * whole-table totals row — goes through here so they agree on that.
 */
export const getCurrencyCell = (value, currency, intl, hasAccess = true) => {
	if (!hasAccess) {
		return <span>-</span>;
	}
	return (
		<span>{Util.GetFormattedCurrencySymbol(currency, intl.formatNumber(value ?? 0, {format: 'always_two_decimal'}))}</span>
	);
};
